import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, ChevronDown, Sparkles, RotateCw, X, ArrowRight, Download, MessageSquare, BarChart3, MapPin } from 'lucide-react';
import { streamDashboardDailyBriefing } from '../../utils/api';
import { parsePartialBriefing } from '../../utils/partialBriefing';
import { useTheme } from '../../context/ThemeContext';
import './DashboardDailyBriefing.css';

// Build the invisible preamble that rides on the user's first message so the AI
// can answer the follow-up grounded in the card the user clicked into. Kept
// compact — narrative + facts + portfolio table + scope. byLocation is omitted
// unless the user explicitly asks; the AI can call SQL tools if it needs more.
function buildBriefingContextPrefix({ card, headline, summary, propertyCount, systemCount, generatedAt }) {
  if (!card) return '';
  const lines = ['[AI DAILY BRIEFING CONTEXT — you are helping the user follow up on today\'s briefing.]'];
  if (headline) lines.push(`Top-level thesis: ${headline}`);
  if (summary) lines.push(`Briefing summary: ${summary}`);
  if (generatedAt) {
    try { lines.push(`Briefing generated: ${new Date(generatedAt).toLocaleString()}`); } catch { /* ignore */ }
  }
  lines.push('');
  const tag = card.tag ? ` (${card.tag})` : '';
  lines.push(`Section the user is asking about: "${card.title}"${tag}`);
  if (card.metric?.label) lines.push(`Headline metric: ${card.metric.label} (trend: ${card.metric.trend || 'neutral'})`);
  if (card.narrative) lines.push(`Narrative: ${card.narrative}`);
  const facts = Array.isArray(card.facts) && card.facts.length ? card.facts : null;
  if (facts) {
    lines.push(`Key facts: ${facts.map(([k, v]) => `${k}: ${v}`).join(' · ')}`);
  }
  const d = card.details;
  if (d && Array.isArray(d.columns) && Array.isArray(d.rows) && d.rows.length) {
    lines.push('');
    lines.push('Portfolio details table:');
    lines.push(d.columns.join(' | '));
    for (const row of d.rows) {
      lines.push(row.map(c => (c == null ? '' : String(c))).join(' | '));
    }
    if (Array.isArray(d.totalRow) && d.totalRow.length === d.columns.length) {
      lines.push(d.totalRow.map(c => (c == null ? '' : String(c))).join(' | '));
    }
  }
  const scope = [
    propertyCount != null ? `${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'}` : null,
    systemCount != null ? `${systemCount} system${systemCount === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');
  if (scope) {
    lines.push('');
    lines.push(`Scope: ${scope}`);
  }
  lines.push('');
  lines.push('Answer the user\'s follow-up grounded in this context. If they need deeper breakdowns not present above, use your SQL tools normally.');
  lines.push('[END BRIEFING CONTEXT]');
  lines.push('');
  lines.push('');  // blank line before the user's actual question
  return lines.join('\n');
}

// Fixed card order the model emits (see getConstellationSystemInstructions('dailyBriefing')
// in openaiHelper_sql.js). While streaming we render a skeleton tile for each slot,
// then swap in the real tile the moment its JSON object finishes parsing.
const EXPECTED_CARDS = [
  { id: 'yesterday-revenue', title: "Yesterday's Revenue" },
  { id: 'last-night-pickup', title: "Last Night's Pickup" },
  { id: 'labor', title: 'Labor' },
  { id: 'tonight', title: 'On Property Tonight' },
  { id: 'month-outlook', title: 'This Month' },
];

// Older cached briefings used a "Good morning — …" title prefix from the pre-redesign
// prompt. Strip the greeting + trailing date so the remaining thesis can take the
// editorial-headline slot.
function headlineFromLegacyTitle(title) {
  if (!title) return null;
  let s = title.replace(/^Good (morning|afternoon|evening)[\s—-]+/i, '').trim();
  s = s.replace(/[\s—-]+(for\s+)?\d{4}-\d{2}-\d{2}$/i, '').trim();
  return s || null;
}

// Subtitle arrives as "pulled from 4 connected systems across 22 properties" for
// older cached briefings. Parse counts back out so the footer chain still renders.
function parseCountsFromSubtitle(subtitle) {
  if (typeof subtitle !== 'string') return { systemCount: null, propertyCount: null };
  const sysMatch = subtitle.match(/(\d+)\s+(?:connected\s+)?systems?/i);
  const propMatch = subtitle.match(/(\d+)\s+propert(?:y|ies)/i);
  return {
    systemCount: sysMatch ? parseInt(sysMatch[1], 10) : null,
    propertyCount: propMatch ? parseInt(propMatch[1], 10) : null,
  };
}

function formatShortDate(d) {
  try { return new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric' }); }
  catch { return String(d); }
}

// AI trend → .val / .bc-verdict tone class.
function toneFromTrend(trend) {
  switch (trend) {
    case 'up':      return 'pos';
    case 'down':    return 'neg';
    case 'warning': return 'warn';
    default:        return 'neu';
  }
}

// Render a narrative string with inline .val pills for every signed numeric token
// (and any unsigned anchor token from the passed-in metrics). `**markdown bold**`
// runs render as <b> for plain anchor numbers that shouldn't be pilled.
function renderNarrative(narrative, metrics) {
  if (!narrative) return null;
  const marks = [];
  // Signed tokens — the negative-lookbehind avoids matching "-04" inside a date.
  const SIGNED = /(?<![0-9])[+\-−]\$?\d[\d,]*(?:\.\d+)?%?(?:\s*pts?)?/g;
  let m;
  while ((m = SIGNED.exec(narrative)) !== null) {
    const isUp = m[0][0] === '+';
    marks.push({ kind: 'pill', start: m.index, end: m.index + m[0].length, text: m[0], tone: isUp ? 'pos' : 'neg' });
  }
  // Card anchor tokens (e.g. "21.6% of revenue" → token "21.6%") picked up
  // automatically with the card's trend tone even though they're unsigned.
  const metricList = metrics ? (Array.isArray(metrics) ? metrics : [metrics]) : [];
  for (const metric of metricList) {
    const label = metric?.label;
    if (!label) continue;
    const mm = label.match(/~?[+\-−]?\$?[\d,]+(?:\.\d+)?%?/);
    if (!mm) continue;
    const token = mm[0];
    const idx = narrative.indexOf(token);
    if (idx !== -1 && !marks.some(x => x.start === idx)) {
      marks.push({ kind: 'pill', start: idx, end: idx + token.length, text: token, tone: toneFromTrend(metric.trend) });
    }
  }
  // Markdown bold — reserved for plain anchor totals the model wants emphasized
  // without turning them into pills.
  const BOLD = /\*\*([^*]+)\*\*/g;
  while ((m = BOLD.exec(narrative)) !== null) {
    marks.push({ kind: 'bold', start: m.index, end: m.index + m[0].length, text: m[1] });
  }
  if (!marks.length) return narrative;
  marks.sort((a, b) => a.start - b.start);
  const out = [];
  let cursor = 0;
  let key = 0;
  for (const mt of marks) {
    if (mt.start < cursor) continue;
    if (mt.start > cursor) out.push(narrative.slice(cursor, mt.start));
    if (mt.kind === 'bold') {
      out.push(<b key={key++}>{mt.text}</b>);
    } else {
      out.push(<span key={key++} className={`val ${mt.tone}`}>{mt.text}</span>);
    }
    cursor = mt.end;
  }
  if (cursor < narrative.length) out.push(narrative.slice(cursor));
  return out;
}

// For a card tile's fact grid: use card.facts if the model provided them; otherwise
// synthesize 4 facts from the card's details table. The totalRow is always useful
// as a fact ("Total Revenue: $X"), followed by up to 3 data rows.
function factsForCard(card) {
  if (Array.isArray(card.facts) && card.facts.length >= 2) {
    return card.facts.slice(0, 4).map(f => Array.isArray(f) ? f : [String(f), '']);
  }
  const d = card.details;
  if (!d || !Array.isArray(d.columns) || !Array.isArray(d.rows)) return [];
  const facts = [];
  const valueColIdx = d.columns.length - 1; // last column (usually variance or total)
  if (Array.isArray(d.totalRow) && d.totalRow.length > 1) {
    facts.push([String(d.totalRow[0] || 'Total'), String(d.totalRow[valueColIdx] ?? d.totalRow[1] ?? '')]);
  }
  for (const row of d.rows) {
    if (!Array.isArray(row) || row.length < 2) continue;
    facts.push([String(row[0] ?? ''), String(row[Math.min(1, row.length - 1)] ?? '')]);
    if (facts.length >= 4) break;
  }
  return facts.slice(0, 4);
}

// A cell in a details-table row may be a string like "+$45,022 (+26.6%)" or a
// plain number like "$214,322". Parse sign to pick the .var-cell tone and split
// off the trailing "(XX%)" for styling.
const VAR_CELL_RE = /^([+\-−][^\s(]+)(?:\s*\(([+\-−][^)]+)\))?$/;
function renderTableCell(cell) {
  const s = String(cell ?? '');
  const m = s.match(VAR_CELL_RE);
  if (!m) return { className: 'val-cell', content: s };
  const sign = m[1][0];
  const tone = sign === '+' ? 'pos' : 'neg';
  const pct = m[2];
  return {
    className: `var-cell ${tone}`,
    content: pct ? <>{m[1]}<span className="pct">({pct})</span></> : m[1],
  };
}

/* ============================================================
 * Clickable card tile (expanded-view grid)
 * ========================================================== */

function BriefingCard({ card, onOpen }) {
  const tone = toneFromTrend(card?.metric?.trend);
  const verdictLabel = card?.tag || card?.metric?.label || '';
  const isFlag = card?.metric?.trend === 'warning';
  const facts = factsForCard(card);
  const drillLabel = card?.actions?.[0]?.label || 'View detail';

  return (
    <div
      className="bc-card card-in is-clickable"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
      }}
    >
      <div className="bc-head">
        <div className="bc-title-row">
          <span className="bc-title">{card.title}</span>
          {verdictLabel && (
            <span className={`bc-verdict ${tone}${isFlag ? ' flag' : ''}`}>
              {isFlag && <span className="bc-flag-dot" />}
              {verdictLabel}
            </span>
          )}
        </div>
        <button
          type="button"
          className="bc-expand-btn"
          aria-label="Open details"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
        >
          <ChevronRight size={12} />
        </button>
      </div>
      {card.narrative && (
        <div className="bc-lead">{renderNarrative(card.narrative, card.metric)}</div>
      )}
      {facts.length > 0 && (
        <div className="bc-facts">
          {facts.map(([k, v], i) => (
            <div key={i}>
              <div className="bc-fact-k">{k}</div>
              <div className="bc-fact-v">{v}</div>
            </div>
          ))}
        </div>
      )}
      <div className="bc-cta">{drillLabel} <span>→</span></div>
    </div>
  );
}

function BriefingCardSkeleton({ title, index = 0 }) {
  return (
    <div className="bc-card bc-skeleton" style={{ animationDelay: `${index * 120}ms` }} aria-busy="true">
      <div className="bc-head">
        <div className="bc-title-row" style={{ flex: 1 }}>
          <span className="bc-title" style={{ opacity: 0.7 }}>{title}</span>
          <span className="sk-line sk-w-70" />
        </div>
      </div>
      <div className="bc-lead">
        <span className="sk-line sk-w-100" />
        <span className="sk-line sk-w-85" style={{ marginTop: 6 }} />
      </div>
      <div className="bc-facts">
        {[0, 1, 2, 3].map(i => (
          <div key={i}>
            <span className="sk-line sk-w-55" />
            <span className="sk-line sk-w-80" style={{ marginTop: 4 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
 * Section detail modal (opens when a tile is clicked)
 * ========================================================== */

function SectionDetailModal({ card, meta, onClose, onHandoff }) {
  const hasLocation = Boolean(card?.details?.byLocation?.rows?.length);
  const [view, setView] = useState('portfolio');
  const [wide, setWide] = useState(false);
  const tone = toneFromTrend(card?.metric?.trend);
  const isFlag = card?.metric?.trend === 'warning';
  const facts = factsForCard(card);
  const drillLabel = card?.actions?.[0]?.label || 'Drill into detail';
  const drillPrompt = card?.actions?.[0]?.prompt || `Tell me more about ${card?.title || 'this section'}.`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const source = view === 'location' && hasLocation
    ? card.details.byLocation
    : card.details;
  const columns = source?.columns || [];
  const rows = source?.rows || [];
  const totalRow = source?.totalRow;

  const updatedStamp = meta?.generatedAt
    ? `Updated ${new Date(meta.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    : '';

  // Portal to body — the dashboard's sidebar + scroll container create their
  // own stacking contexts, so z-index alone won't lift the modal above them.
  // This mirrors how other modals in the app (FileViewerModal, ForecastEditModal,
  // DailyForecastModal, etc.) solve the same problem.
  return createPortal((
    <div className="modal-scrim" onClick={onClose}>
      <div
        className={`modal-shell${wide ? ' expanded' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="section-modal-title"
      >
        <div className="modal-head">
          <div className="modal-head-left">
            <div className="modal-eyebrow">
              <Sparkles size={11} />
              AI Daily Briefing · Section detail
            </div>
            <div className="modal-title-row">
              <h2 id="section-modal-title" className="modal-title">{card.title}</h2>
              {(card.tag || card.metric?.label) && (
                <span className={`bc-verdict ${tone}${isFlag ? ' flag' : ''}`}>
                  {isFlag && <span className="bc-flag-dot" />}
                  {card.tag || card.metric?.label}
                </span>
              )}
            </div>
            {card.narrative && (
              <p className="modal-lead">{renderNarrative(card.narrative, card.metric)}</p>
            )}
          </div>
          <div className="modal-head-actions">
            <button className="btn-sm" onClick={() => setWide(w => !w)} aria-label="Toggle wide view">
              <ArrowRight size={12} />
              {wide ? 'Collapse' : 'Wide'}
            </button>
            <button className="modal-close" onClick={onClose} aria-label="Close">
              <X size={14} />
            </button>
          </div>
        </div>

        {facts.length > 0 && (
          <div className="modal-facts">
            {facts.map(([k, v], i) => (
              <div key={i} className="modal-fact">
                <div className="modal-fact-k">{k}</div>
                <div className="modal-fact-v">{v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="modal-toolbar">
          <div className="view-toggle" role="tablist">
            <button
              role="tab"
              aria-selected={view === 'portfolio'}
              className={view === 'portfolio' ? 'active' : ''}
              onClick={() => setView('portfolio')}
            >
              <BarChart3 size={12} /> Portfolio
            </button>
            <button
              role="tab"
              aria-selected={view === 'location'}
              className={view === 'location' ? 'active' : ''}
              onClick={() => hasLocation && setView('location')}
              disabled={!hasLocation}
              title={hasLocation ? undefined : 'No per-property breakdown for this card'}
            >
              <MapPin size={12} /> By Location
            </button>
          </div>
          <div className="modal-toolbar-meta">
            {view === 'portfolio' ? 'Portfolio roll-up' : `${rows.length} properties`}
          </div>
        </div>

        <div className="modal-table-wrap">
          <div className="modal-table-eyebrow">
            <span>Data table</span>
            <div className="modal-table-head-actions">
              <button className="icon-btn" aria-label="Download" title="Download CSV">
                <Download size={12} />
              </button>
            </div>
          </div>
          {columns.length > 0 ? (
            <table className="modal-table">
              <thead>
                <tr>
                  {columns.map((h, i) => (
                    <th key={i} className={i === 0 ? 'first' : ''}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => {
                      if (c === 0) {
                        return <td key={c} className="metric-name">{cell}</td>;
                      }
                      const { className, content } = renderTableCell(cell);
                      return <td key={c} className={className}>{content}</td>;
                    })}
                  </tr>
                ))}
                {Array.isArray(totalRow) && totalRow.length === columns.length && (
                  <tr className="total-row">
                    {totalRow.map((cell, c) => {
                      if (c === 0) return <td key={c} className="metric-name">{cell}</td>;
                      const { className, content } = renderTableCell(cell);
                      return <td key={c} className={className}>{content}</td>;
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="modal-muted" style={{ padding: '16px 0' }}>
              No table data available for this section.
            </div>
          )}
        </div>

        <div className="modal-foot">
          <span className="modal-muted">{meta?.generatedAt ? updatedStamp : ''}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn-sm"
              onClick={() => onHandoff && onHandoff({ card, prompt: `About "${card.title}": `, focusComposer: true })}
            >
              <MessageSquare size={12} />
              Ask in chat
            </button>
            <button
              type="button"
              className="btn-sm primary"
              onClick={() => onHandoff && onHandoff({ card, prompt: drillPrompt })}
            >
              {drillLabel}
              <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}

/* ============================================================
 * Main component
 * ========================================================== */

const DashboardDailyBriefing = ({ hotelId, hotelName, currency, refreshKey, comparisonMode, comparisonLabel }) => {
  const { isDarkMode } = useTheme();
  void isDarkMode; // reserved for future dark-mode tweaks per-card
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('all');
  const [progressive, setProgressive] = useState(null);
  const [forceKey, setForceKey] = useState(0);
  const [openCard, setOpenCard] = useState(null);

  const tokenBufRef = useRef('');
  const regeneratingRef = useRef(false);
  const hasDataRef = useRef(false);

  useEffect(() => {
    let abort = null;
    let cancelled = false;

    setLoading(true);
    setStreaming(false);
    setError(null);
    setData(null);
    setMeta(null);
    setStreamStatus(null);
    setRegenerating(null);
    setProgressive(null);
    tokenBufRef.current = '';
    regeneratingRef.current = false;
    hasDataRef.current = false;

    const currentOrgId = localStorage.getItem('currentOrgId');

    abort = streamDashboardDailyBriefing(
      { hotelId: hotelId || 'all', currentOrgId, currency, force: forceKey > 0, comparisonMode, comparisonLabel },
      {
        onCached: ({ data: d, generatedAt, model }) => {
          if (cancelled) return;
          setData(d);
          setMeta({ cached: true, generatedAt, model });
          setLoading(false);
          hasDataRef.current = true;
        },
        onRegenerating: (payload) => {
          if (cancelled) return;
          setRegenerating({
            reason: payload?.reason || 'data-grew',
            delta: payload?.delta,
          });
          regeneratingRef.current = true;
          tokenBufRef.current = '';
        },
        onStatus: (msg) => {
          if (cancelled) return;
          setStreamStatus(msg);
        },
        onToken: (tok) => {
          if (cancelled) return;
          if (!streaming) setStreaming(true);
          tokenBufRef.current += tok;
          const partial = parsePartialBriefing(tokenBufRef.current);
          if (partial && (partial.header || partial.summary || partial.cards?.length)) {
            setProgressive(partial);
          }
        },
        onDone: (payload) => {
          if (cancelled) return;
          if (payload?.data) {
            setData(payload.data);
            setMeta({ cached: !!payload.cached, generatedAt: payload.generatedAt, model: payload.model });
            hasDataRef.current = true;
          }
          setLoading(false);
          setStreaming(false);
          setRegenerating(null);
          setProgressive(null);
          regeneratingRef.current = false;
        },
        onError: (msg) => {
          if (cancelled) return;
          if (regeneratingRef.current || hasDataRef.current) {
            setRegenerating(null);
            setStreaming(false);
            regeneratingRef.current = false;
          } else {
            setError(msg || 'Briefing failed');
            setLoading(false);
            setStreaming(false);
          }
        },
      }
    );

    return () => {
      cancelled = true;
      if (abort) abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId, currency, refreshKey, forceKey, comparisonMode, comparisonLabel]);

  const effectiveData = data || progressive;
  const hasAnyContent = Boolean(effectiveData?.header || effectiveData?.summary || effectiveData?.cards?.length);
  const isFinal = Boolean(data);

  const cards = useMemo(
    () => Array.isArray(effectiveData?.cards) ? effectiveData.cards : [],
    [effectiveData]
  );
  const flagCount = useMemo(
    () => cards.filter(c => c.metric?.trend === 'warning' || c.metric?.trend === 'down').length,
    [cards]
  );

  // While streaming, render the known slots in order and substitute a skeleton
  // for cards the parser hasn't reached yet. Once `data` lands, trust it.
  const orderedItems = useMemo(() => {
    if (isFinal) return cards.map(c => ({ kind: 'card', card: c }));
    const byId = new Map(cards.map(c => [c.id, c]));
    return EXPECTED_CARDS.map(exp => {
      const real = byId.get(exp.id);
      return real ? { kind: 'card', card: real } : { kind: 'skeleton', id: exp.id, title: exp.title };
    });
  }, [cards, isFinal]);

  const filteredItems = useMemo(() => {
    if (filter !== 'flags') return orderedItems;
    return orderedItems.filter(item => {
      if (item.kind !== 'card') return false;
      const t = item.card.metric?.trend;
      return t === 'down' || t === 'warning';
    });
  }, [orderedItems, filter]);

  const sectionCount = isFinal ? cards.length : Math.max(EXPECTED_CARDS.length, cards.length);

  const headline = effectiveData?.header?.headline
    || headlineFromLegacyTitle(effectiveData?.header?.title);

  const { systemCount: parsedSystems, propertyCount: parsedProps } = useMemo(
    () => parseCountsFromSubtitle(effectiveData?.header?.subtitle),
    [effectiveData?.header?.subtitle]
  );
  const propertyCount = effectiveData?.header?.propertyCount ?? parsedProps;
  const systemCount = effectiveData?.header?.systemCount ?? parsedSystems;

  const updatedTime = meta?.generatedAt
    ? new Date(meta.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;
  const updatedDate = meta?.generatedAt ? formatShortDate(meta.generatedAt) : null;

  const handleRegenerate = () => {
    if (streaming) return;
    setForceKey(k => k + 1);
  };

  // "Ask in chat" opens the one persistent assistant with a one-shot briefing
  // preamble; no route change and no second chat component.
  const handleHandoff = ({ card, prompt, focusComposer }) => {
    const contextPrefix = buildBriefingContextPrefix({
      card,
      headline: effectiveData?.header?.headline || headlineFromLegacyTitle(effectiveData?.header?.title),
      summary: effectiveData?.summary,
      propertyCount,
      systemCount,
      generatedAt: meta?.generatedAt,
    });
    setOpenCard(null);
    window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', { detail: {
      expanded: true,
      hotelId: hotelId || 'all',
      prompt: prompt || '',
      contextPrefix,
      focusComposer: !!focusComposer,
    } }));
  };

  const isStreamingClass = streaming ? ' is-streaming' : '';

  return (
    <div className="ddb-root">
      <div className={`briefing${isStreamingClass}`}>
        <div className="briefing-head">
          <div className="briefing-label">
            <span className={`sparkle-ic${streaming ? ' pulsing' : ''}`}>
              <Sparkles size={13} />
            </span>
            AI Daily Briefing
            {streaming && <span className="live-dot" title="Streaming"><span /></span>}
            {regenerating && !streaming && (
              <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 600, color: 'var(--lodge-orange-500)' }}>
                Regenerating…
              </span>
            )}
          </div>
          <div className="briefing-meta">
            {(updatedDate || updatedTime) && (
              <span>
                {updatedDate}
                {updatedDate && updatedTime && ' · Updated '}
                {!updatedDate && updatedTime && 'Updated '}
                {updatedTime}
              </span>
            )}
            {streaming && <span>Generating live…</span>}
            <button
              type="button"
              className={`regen-btn${streaming ? ' is-spinning' : ''}`}
              onClick={handleRegenerate}
              disabled={streaming}
              aria-label="Regenerate briefing"
            >
              <RotateCw size={12} />
              {streaming ? 'Generating…' : 'Regenerate'}
            </button>
          </div>
        </div>

        <div className="briefing-body">
          {loading && !hasAnyContent && !error && (
            <div className="ddb-loading">
              <span>{streamStatus || 'Building your daily briefing…'}</span>
            </div>
          )}

          {error && !hasAnyContent && <div className="ddb-error">{error}</div>}

          {hasAnyContent && !error && (
            <>
              {headline ? (
                <h2 className="briefing-title">{headline}</h2>
              ) : (
                <div className="ddb-skel-title-line" />
              )}

              {effectiveData.summary ? (
                <p className="briefing-text">
                  {renderNarrative(effectiveData.summary, cards.map(c => c.metric).filter(Boolean))}
                </p>
              ) : streaming ? (
                <div className="ddb-skel-summary">
                  <span className="ddb-skel-line" />
                  <span className="ddb-skel-line" />
                  <span className="ddb-skel-line ddb-skel-line--short" />
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="briefing-foot">
          <span>
            {propertyCount != null && <>{propertyCount} {propertyCount === 1 ? 'property' : 'properties'}<span className="dot">·</span></>}
            {systemCount != null && <>{systemCount} system{systemCount === 1 ? '' : 's'}<span className="dot">·</span></>}
            <b>{sectionCount}</b> section{sectionCount === 1 ? '' : 's'}
            {flagCount > 0 && (
              <>
                <span className="dot">·</span>
                <span className="flag-count">{flagCount} flag{flagCount === 1 ? '' : 's'}</span>
              </>
            )}
          </span>
          <div className="briefing-actions">
            <div className="briefing-filter" role="tablist" aria-label="Card filter">
              <button type="button" className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
              <button type="button" className={filter === 'flags' ? 'active' : ''} onClick={() => setFilter('flags')}>Flags</button>
            </div>
            {(sectionCount > 0 || streaming) && (
              <button type="button" className="briefing-expand" onClick={() => setExpanded(v => !v)}>
                {expanded ? 'Collapse' : 'Expand full briefing'}
                <ChevronDown
                  size={12}
                  style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms' }}
                />
              </button>
            )}
          </div>
        </div>

        {streaming && <div className="briefing-progress"><div className="briefing-progress-bar" /></div>}
      </div>

      {expanded && hasAnyContent && !error && filteredItems.length > 0 && (
        <div className="briefing-cards">
          {filteredItems.map((item, i) => (
            item.kind === 'card'
              ? (
                <BriefingCard
                  key={item.card.id || item.card.title}
                  card={item.card}
                  onOpen={() => setOpenCard(item.card)}
                />
              )
              : (
                <BriefingCardSkeleton key={`skel-${item.id}`} title={item.title} index={i} />
              )
          ))}
        </div>
      )}

      {openCard && (
        <SectionDetailModal
          card={openCard}
          meta={meta}
          onClose={() => setOpenCard(null)}
          onHandoff={handleHandoff}
        />
      )}
    </div>
  );
};

export default DashboardDailyBriefing;
